import React, { useState, useEffect } from 'react';

const inputCls = "w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const labelCls = "block text-slate-700 text-sm font-medium mb-1.5";

const EditPaymentModal = ({ payment, onClose, onSave }) => {
  const [formData, setFormData] = useState({ amount: '', paymentMethod: '', paidDate: '', status: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount || '',
        paymentMethod: payment.payment_method || '',
        paidDate: payment.paid_date ? new Date(payment.paid_date).toISOString().split('T')[0] : '',
        status: payment.status || '',
        notes: payment.notes || ''
      });
    }
  }, [payment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onSave(payment.id, { ...formData, paidDate: formData.paidDate || null });
    } catch (err) {
      setError(err.message || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  if (!payment) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-slate-900">Edit Payment Record</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Amount (£)</label>
            <input type="number" step="0.01" value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Payment Method</label>
            <select value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              className={inputCls} required>
              <option value="">Select method</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="standing_order">Standing Order</option>
              <option value="direct_debit">Direct Debit</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>
              Paid Date {formData.status === 'paid' && <span className="text-red-500">*</span>}
            </label>
            <input type="date" value={formData.paidDate}
              onChange={(e) => setFormData({ ...formData, paidDate: e.target.value })}
              className={inputCls} />
            {formData.status !== 'paid' && (
              <p className="text-slate-400 text-xs mt-1">Leave empty for pending/failed payments</p>
            )}
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className={inputCls} required>
              <option value="">Select status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className={inputCls} rows="3" maxLength="500" />
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-all disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-700 text-sm">⚠️ This action will be logged in the audit trail.</p>
        </div>
      </div>
    </div>
  );
};

export default EditPaymentModal;
